import React, { useState } from 'react';

import { Box, Loader, Paper, Stack, Text, TextInput, Title, UnstyledButton } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import useAutoCompleteClassType from 'hooks/classType/useAutoCompleteClassType';

import { useGLMCardStyles } from '../styles';
import { FormValues } from '../types';

interface IKiloGLMClassTypeSelectProps {
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>;
}

const KiloGLMClassTypeSelect = ({ form }: IKiloGLMClassTypeSelectProps): JSX.Element => {
  const { classes, cx } = useGLMCardStyles();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useAutoCompleteClassType({ search: debouncedSearch });
  const classTypes = data?.data?.results?.map(c => ({ value: c.kiloClassTypeId, label: c.name })) ?? [];

  const toggleClassType = (id: string, label: string): void => {
    const currentIds = form.values.classTypeIds || [];
    const currentLabels = form.values.classTypeLabels || {};
    if (currentIds.includes(id)) {
      const remainingLabels = { ...currentLabels };
      delete remainingLabels[id];
      form.setValues(prev => ({
        ...prev,
        classTypeIds: currentIds.filter(v => v !== id),
        classTypeLabels: remainingLabels,
      }));
    } else {
      form.setValues(prev => ({
        ...prev,
        classTypeIds: [...currentIds, id],
        classTypeLabels: { ...currentLabels, [id]: label },
      }));
    }
  };

  return (
    <Paper>
      <Stack sx={{ paddingTop: '20px' }}>
        <Title order={3}>Select Class Types</Title>
        <Text size={15}>Select the class types that trigger the flow when an athlete checks in.</Text>
        <TextInput
          placeholder='Search class types'
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          icon={<IconSearch size={14} />}
          rightSection={isLoading ? <Loader size={14} /> : null}
        />
        <Stack spacing={6}>
          {classTypes.map(classType => {
            const isSelected = (form.values.classTypeIds || []).includes(classType.value);
            return (
              <UnstyledButton
                key={classType.value}
                onClick={() => toggleClassType(classType.value, classType.label)}
              >
                <Box className={cx(classes.card, { [classes.cardSelected]: isSelected })}>
                  <Text weight={isSelected ? 600 : 400}>{classType.label}</Text>
                </Box>
              </UnstyledButton>
            );
          })}
          {!isLoading && classTypes.length === 0 && (
            <Text size='sm' color='dimmed'>
              No class types found
            </Text>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default KiloGLMClassTypeSelect;
